import React, { useMemo } from 'react';

import Suspense from '@atlaskit/react-ufo/suspense';
import { History } from 'history';
import { graphql, PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { checkUserHasCapabilities } from '@townsquare/capability-provider/checkUserHasCapabilities';
import { type CollaborationContext } from '@townsquare/collab-context';
import { filterFalsyStrings, filterNull } from '@townsquare/filter-type';
import { matchRoute } from '@townsquare/router/primitives/match-route';
import type { MatchedRoute } from '@townsquare/router/routing-types';
import type { CapabilityRouterFragment$key } from '@townsquare/router/types';
import { userAccessRedirectContextHolder } from '@townsquare/router/user-access';
import { createSearchParamsFromCurrentUrl } from '@townsquare/search-params';
import { FullScreenSpaLoader } from '@townsquare/spa-loader';
import { User } from '@townsquare/user-store/types';
import { useWorkspaceActions } from '@townsquare/workspace-store/actions';
import { WorkspaceDetails } from '@townsquare/workspace-store/types';

import { routes } from '../router/routes';

import { BootstrapShell, type BootstrapShellData } from './BootstrapShell';
import { BootstrapErrorBoundary } from './ErrorBoundary/BootstrapErrorBoundary';
import { NotFoundScreen } from './ErrorBoundary/scenarios';
import { BootstrapQuery } from './__generated__/BootstrapQuery.graphql';
import { deriveContextCloudId } from './deriveBootstrap';

export type BootstrapResult = {
	orgId?: string;
	cloudIds: string[];
	workspaceIds: string[];
	workspaceUuids: string[];
	workspace?: WorkspaceDetails;
	user: User;
	relayKey: CapabilityRouterFragment$key | undefined;
	collabContext?: CollaborationContext | null;
};

type BootstrapProps = {
	children: (data: BootstrapResult) => JSX.Element;
	matchedRoute: MatchedRoute | null;
	history: History<unknown>;
	queryRef: PreloadedQuery<BootstrapQuery>;
};

/**
 * GraphQL populator for the home bootstrap — does the Relay-specific work
 * (unmask preloaded query, call fragment-ref versions of `initWorkspaceFromHome`
 * and `checkUserHasCapabilities`), then hands the flattened plain-data shape
 * to `BootstrapShell` for everything else.
 *
 * WHAT STAYS HERE: anything that needs the Relay data source (`usePreloadedQuery`,
 * fragment-ref-taking helpers). WHAT GOES TO THE SHELL: all branch-agnostic
 * side effects (org context, collab context, statsig init, monitoring, route
 * preload, app-state transition).
 */
function BootstrapResolution(props: BootstrapProps): JSX.Element {
	const data = usePreloadedQuery<BootstrapQuery>(
		graphql`
			query BootstrapQuery($context: OrganizationContextInput) {
				bootstrap(organizationContext: $context) {
					orgId
					cloudIds
					collabContext {
						cloudIds
						isMultiSite
						collabContextSiteAris
						hasMultipleUnits
					}
					userIsOrgAdmin
					workspaces {
						id
						uuid
						cloudId
						type
						...storeHomeWorkspace
						...CapabilityRouterFragment
						...utils_checkUserHasCapabilities
					}
				}
			}
		`,
		props.queryRef,
	);

	const orgId = data.bootstrap?.orgId ?? undefined;
	// All derivations memoised — shellData carries these into setOrgContext
	// and downstream consumers; stable refs avoid spurious re-runs.
	// Master had `orgCloudIds: ... ?? null`; preserve.
	const orgCloudIds = useMemo(
		() => data.bootstrap?.cloudIds?.filter(filterFalsyStrings) ?? null,
		[data.bootstrap?.cloudIds],
	);
	const cloudIds = useMemo(() => orgCloudIds ?? [], [orgCloudIds]);
	const workspaces = useMemo(
		() => data.bootstrap?.workspaces?.filter(filterNull) ?? [],
		[data.bootstrap?.workspaces],
	);
	const workspaceIds = useMemo(
		() => workspaces.map((workspace) => workspace.id).filter(filterFalsyStrings),
		[workspaces],
	);
	const workspaceUuids = useMemo(
		() => workspaces.map((workspace) => workspace.uuid).filter(filterFalsyStrings),
		[workspaces],
	);
	const collabContextCloudIds = useMemo(
		() => data.bootstrap?.collabContext?.cloudIds?.filter(filterFalsyStrings) ?? [],
		[data.bootstrap?.collabContext?.cloudIds],
	);
	const collabContextSiteAris = useMemo(
		() => data.bootstrap?.collabContext?.collabContextSiteAris?.filter(filterNull) ?? [],
		[data.bootstrap?.collabContext?.collabContextSiteAris],
	);

	/**
	 * Titan is site-scoped
	 * The ordering here is as follows:
	 * 1. If the query has a context (aka we are hitting a deep link and we're passing a cloudId to the query), use that for searching for the workspace
	 * 2. If the query has no context, use the first cloudId in the list as that is ordered by the backend to be the most relevant (preference or otherwise based on the user)
	 * 3. If there are no cloudIds, default to null (aka no workspace exists for the current request)
	 *
	 * Scenario 1: User is on a deep link and the context is passed in the query
	 * The org has two sites, A and B.
	 * A has Titan, B does not.
	 * The user is on a deep link for site B.
	 * We won't find a workspace in this scenario, since we're only searching for the cloudId of site B, but the query will return the workspace for site A.
	 */
	const contextCloudId = useMemo(
		() => deriveContextCloudId(props.queryRef.variables.context?.cloudId ?? undefined, cloudIds),
		[props.queryRef.variables.context?.cloudId, cloudIds],
	);
	const workspace = useMemo(
		() =>
			workspaces
				// Exclude PRODUCT workspaces, treat them as no workspace
				.filter((w) => w.type === 'GLOBAL_EXPERIENCE')
				.find((w) => w.cloudId === contextCloudId),
		[workspaces, contextCloudId],
	);

	const workspaceActions = useWorkspaceActions();
	let workspaceDetails: WorkspaceDetails | undefined;
	if (workspace && orgId) {
		workspaceDetails = workspaceActions.initWorkspaceFromHome(workspace, {
			numWorkspaces: workspaceIds.length,
			userIsOrgAdmin: data.bootstrap?.userIsOrgAdmin ?? false,
			organisationId: orgId,
		});
	}

	const userHasProjectAccess = workspace
		? checkUserHasCapabilities(workspace, ['projects_app:access'], true)
		: false;
	const userHasGoalAccess = workspace
		? checkUserHasCapabilities(workspace, ['goals_app:access'], true)
		: false;

	const collabContextSource = data.bootstrap?.collabContext
		? {
				cloudIds: collabContextCloudIds,
				isMultiSite: data.bootstrap.collabContext.isMultiSite,
				hasMultipleUnits: data.bootstrap.collabContext.hasMultipleUnits,
				collabContextSiteAris: collabContextSiteAris,
			}
		: null;

	const shellData: BootstrapShellData = {
		orgId,
		cloudIds,
		orgCloudIds,
		workspaceIds,
		workspaceUuids,
		contextCloudId,
		collabContextSource,
		activeWorkspace: workspace ? { cloudId: workspace.cloudId, uuid: workspace.uuid } : undefined,
		userHasProjectAccess,
		userHasGoalAccess,
	};

	return (
		<BootstrapShell
			bootstrap={shellData}
			workspaceDetails={workspaceDetails}
			matchedRoute={props.matchedRoute}
			history={props.history}
		>
			{({ collabContext, currentUser }) =>
				props.children({
					orgId,
					cloudIds,
					workspaceIds,
					workspaceUuids,
					workspace: workspaceDetails,
					user: currentUser,
					relayKey: workspace,
					collabContext,
				})
			}
		</BootstrapShell>
	);
}

export function Bootstrap(props: Omit<BootstrapProps, 'matchedRoute'>): React.JSX.Element {
	const urlSearch = Object.fromEntries(createSearchParamsFromCurrentUrl());
	const matchedRoute = matchRoute(routes, window.location.pathname, urlSearch);
	userAccessRedirectContextHolder.initUserAccessRedirect(routes);

	if (matchedRoute?.route.name === '404-page') {
		return <NotFoundScreen />;
	}

	return (
		<BootstrapErrorBoundary>
			<Suspense interactionName="spa-loader" fallback={<FullScreenSpaLoader />}>
				<BootstrapResolution {...props} matchedRoute={matchedRoute} />
			</Suspense>
		</BootstrapErrorBoundary>
	);
}
